import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  EyeIcon,
  FlagIcon,
  PencilSquareIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../components/dashboard/hse/HsePageChrome";
import { HseGuideButton, HseModuleGuide } from "../../../components/dashboard/hse/HseModuleGuide";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { HseSetupMissingBanner } from "../../../components/dashboard/hse/HseSetupMissingBanner";
import { HSE_PRINT_STYLES, runWindowPrint } from "../../../components/dashboard/hse/hsePrintStyles";
import {
  approveHseGoal,
  createHseGoal,
  deleteHseGoal,
  exportHseGoalPdf,
  fetchHseGoals,
  signHseGoal,
  updateHseGoal,
} from "../../../api/hse/goals";
import { HseRowActionButton, HseRowActions } from "../../../components/dashboard/hse/HseRowActions";
import { fetchHseTiltak } from "../../../api/hse/tiltak";
import {
  HSE_GOAL_FOCUS_KEYS,
  HSE_GOAL_PURPOSE_KEYS,
  availableGoalYears,
  canApproveHseGoal,
  canSignHseGoal,
  currentGoalYear,
  emptyHseGoal,
  goalsForYear,
  upsertGoalInList,
  yearBounds,
} from "../../../components/dashboard/hse/hseGoalsUtils";

export const HseKsGoals = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const yearFromUrl = Number(searchParams.get("year"));
  const selectedYear = Number.isInteger(yearFromUrl) && yearFromUrl >= 2000
    ? yearFromUrl
    : currentGoalYear();

  const [guideOpen, setGuideOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [goals, setGoals] = useState([]);
  const [tiltakOptions, setTiltakOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [goalsResponse, tiltakResponse] = await Promise.all([
        fetchHseGoals(),
        fetchHseTiltak({ per_page: 50 }).catch(() => ({ data: [] })),
      ]);
      setGoals(goalsResponse.data || []);
      setTiltakOptions(tiltakResponse.data || []);
    } catch (err) {
      setError(err.message || t("hse.goals.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const yearGoals = useMemo(() => goalsForYear(goals, selectedYear), [goals, selectedYear]);
  const years = useMemo(() => availableGoalYears(goals), [goals]);

  const setYear = (year) => {
    const next = new URLSearchParams(searchParams);
    if (Number(year) === currentGoalYear()) next.delete("year");
    else next.set("year", String(year));
    setSearchParams(next, { replace: true });
    setEditingId(null);
    setDraft(null);
  };

  const replaceGoal = (saved) => {
    setGoals((prev) => upsertGoalInList(prev, saved));
  };

  const startEdit = (goal) => {
    setEditingId(goal.id);
    setDraft({ ...goal });
  };

  const createFromTemplate = async ({ title, description, category }) => {
    setBusyId("create");
    try {
      const saved = await createHseGoal({
        ...emptyHseGoal(selectedYear, { category }),
        ...yearBounds(selectedYear),
        title,
        description,
      });
      replaceGoal(saved);
      startEdit(saved);
      toast.success(t("hse.goals.created"));
    } catch {
      toast.error(t("hse.goals.save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const addPurpose = (key) => {
    if (yearGoals.some((item) => item.category === key)) return;
    createFromTemplate({
      title: t(`hse.goals.purpose.chips.${key}`),
      description: "",
      category: key,
    });
  };

  const addFocusFromTemplate = (templateKey) => {
    if (yearGoals.some((item) => item.category === templateKey)) return;
    createFromTemplate({
      title: t(`hse.goals.focus.templates.${templateKey}.title`),
      description: t(`hse.goals.focus.templates.${templateKey}.description`),
      category: templateKey,
    });
  };

  const addBlank = () => {
    createFromTemplate({
      title: t("hse.goals.focus.untitled"),
      description: "",
      category: null,
    });
  };

  const handleSaveDraft = async () => {
    if (!draft?.id) return;
    if (!draft.title.trim()) {
      toast.error(t("hse.goals.title_required"));
      return;
    }
    setBusyId(draft.id);
    try {
      const saved = await updateHseGoal(draft.id, draft);
      replaceGoal(saved);
      setEditingId(null);
      setDraft(null);
      toast.success(t("hse.goals.saved"));
    } catch {
      toast.error(t("hse.goals.save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (goal) => {
    if (!goal?.id) return;
    setBusyId(goal.id);
    try {
      await deleteHseGoal(goal.id);
      setGoals((prev) => prev.filter((item) => item.id !== goal.id));
      if (editingId === goal.id) {
        setEditingId(null);
        setDraft(null);
      }
      toast.success(t("hse.goals.deleted"));
    } catch {
      toast.error(t("hse.goals.delete_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = async (goal) => {
    if (!canApproveHseGoal(goal)) return;
    setBusyId(goal.id);
    try {
      replaceGoal(await approveHseGoal(goal.id));
      toast.success(t("hse.goals.approve.saved"));
    } catch {
      toast.error(t("hse.goals.save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleSign = async (goal) => {
    if (!canSignHseGoal(goal)) return;
    setBusyId(goal.id);
    try {
      replaceGoal(await signHseGoal(goal.id));
      toast.success(t("hse.goals.sign.saved"));
    } catch {
      toast.error(t("hse.goals.save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleExportPdf = async (goal) => {
    if (!goal?.id) return;
    try {
      setExportingId(goal.id);
      await exportHseGoalPdf(goal.id, `hse-goal-${goal.id}.pdf`);
    } catch (err) {
      toast.error(err.message || t("hse.goals.export_failed"));
    } finally {
      setExportingId(null);
    }
  };

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.goals.title")}
      subtitle={t("hse.goals.subtitle", { year: selectedYear })}
      listCount={loading ? null : yearGoals.length}
    >
      <style>{HSE_PRINT_STYLES}</style>
      <div className="hse-print-root mx-auto mt-8 max-w-4xl px-4 pb-28 sm:px-6 lg:px-8">
        <div className="mb-4 flex justify-end print:hidden">
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>
        <HseModuleGuide topic="goals" open={guideOpen} onClose={() => setGuideOpen(false)} />
        <HseSetupMissingBanner />

        <ModuleFilter
          filterCard
          hideStatusFilter
          hideSearch
          initialFilters={{ goal_years: [String(selectedYear)] }}
          onFiltersChange={(next) => {
            const year = Number(next.goal_years?.[0]);
            if (Number.isInteger(year)) setYear(year);
          }}
          customDropdownFilters={[
            {
              key: "goal_years",
              label: t("hse.goals.filter.year"),
              options: years.map((year) => ({
                value: String(year),
                label: String(year),
                class: "bg-slate-100 text-slate-800",
              })),
            },
          ]}
        />

        {loading ? (
          <div className="mt-6">
            <ResourceLoading size="base" />
          </div>
        ) : error ? (
          <div className="mt-6">
            <ResourceError errors={[error]} onRetry={load} />
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-base font-semibold text-slate-900">{t("hse.goals.purpose.title")}</h2>
              <p className="mt-1 text-sm text-slate-600">{t("hse.goals.purpose.help")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {HSE_GOAL_PURPOSE_KEYS.map((key) => {
                  const used = yearGoals.some((item) => item.category === key);
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={used || busyId === "create"}
                      onClick={() => addPurpose(key)}
                      className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                        used
                          ? "bg-[#1463C7] text-white"
                          : "border border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 disabled:opacity-50"
                      }`}
                    >
                      {t(`hse.goals.purpose.chips.${key}`)}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">{t("hse.goals.focus.title")}</h2>
                  <p className="mt-1 text-sm text-slate-600">{t("hse.goals.focus.help")}</p>
                </div>
                <button
                  type="button"
                  onClick={addBlank}
                  disabled={busyId === "create"}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("hse.goals.focus.add")}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {HSE_GOAL_FOCUS_KEYS.map((key) => {
                  const used = yearGoals.some((item) => item.category === key);
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={used || busyId === "create"}
                      onClick={() => addFocusFromTemplate(key)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t(`hse.goals.focus.templates.${key}.title`)}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 space-y-3">
                {yearGoals.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center">
                    <FlagIcon className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-sm text-slate-500">{t("hse.goals.focus.empty")}</p>
                  </div>
                ) : (
                  yearGoals.map((goal) => {
                    const editing = editingId === goal.id && draft;
                    const current = editing ? draft : goal;
                    return (
                      <article
                        key={goal.id}
                        className={`rounded-2xl border border-slate-200 bg-slate-50/60 p-4 ${
                          editing ? "" : "cursor-pointer"
                        }`}
                        onClick={() => {
                          if (!editing) startEdit(goal);
                        }}
                      >
                        {editing ? (
                          <div className="space-y-3">
                            <input
                              value={current.title}
                              onChange={(event) =>
                                setDraft((prev) => ({ ...prev, title: event.target.value }))
                              }
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
                              placeholder={t("hse.goals.focus.title_placeholder")}
                            />
                            <textarea
                              value={current.description}
                              onChange={(event) =>
                                setDraft((prev) => ({ ...prev, description: event.target.value }))
                              }
                              rows={3}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
                              placeholder={t("hse.goals.focus.description_placeholder")}
                            />
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {t("hse.goals.focus.tiltak")}
                              <select
                                value={current.hse_action_id || ""}
                                onChange={(event) =>
                                  setDraft((prev) => ({
                                    ...prev,
                                    hse_action_id: event.target.value ? Number(event.target.value) : null,
                                  }))
                                }
                                className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
                              >
                                <option value="">{t("hse.goals.focus.tiltak_none")}</option>
                                {tiltakOptions.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.title || `#${item.id}`}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(null);
                                  setDraft(null);
                                }}
                                className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700"
                              >
                                {t("hse.goals.focus.cancel")}
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveDraft}
                                disabled={busyId === current.id}
                                className="rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                              >
                                {t("hse.goals.focus.done")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-sm font-semibold text-slate-900">
                                  {goal.title || t("hse.goals.focus.untitled")}
                                </h3>
                                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">
                                  {t(`hse.goals.status.${goal.status}`)}
                                </span>
                              </div>
                              {goal.description ? (
                                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                                  {goal.description}
                                </p>
                              ) : null}
                              {goal.hse_action_id ? (
                                <Link
                                  to={`/dashboard/hse-ks/tiltak/${goal.hse_action_id}`}
                                  onClick={(event) => event.stopPropagation()}
                                  className="mt-2 inline-block text-sm font-semibold text-[#1463C7]"
                                >
                                  {t("hse.goals.focus.tiltak_link")}
                                </Link>
                              ) : null}
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                {goal.approved_at ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    {t("hse.goals.approve.approved_on", {
                                      date: String(goal.approved_at).slice(0, 10),
                                    })}
                                  </span>
                                ) : null}
                                {goal.signed_at ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    {t("hse.goals.sign.signed_on", {
                                      date: String(goal.signed_at).slice(0, 10),
                                    })}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <HseRowActions className="shrink-0">
                              <HseRowActionButton icon={EyeIcon} onClick={() => startEdit(goal)}>
                                {t("hse.goals.open")}
                              </HseRowActionButton>
                              <HseRowActionButton icon={PencilSquareIcon} onClick={() => startEdit(goal)}>
                                {t("hse.goals.focus.edit")}
                              </HseRowActionButton>
                              <HseRowActionButton
                                icon={ArrowDownTrayIcon}
                                disabled={exportingId === goal.id}
                                onClick={() => handleExportPdf(goal)}
                              >
                                {exportingId === goal.id
                                  ? t("hse.goals.exporting")
                                  : t("hse.goals.export_pdf")}
                              </HseRowActionButton>
                              {canApproveHseGoal(goal) ? (
                                <HseRowActionButton
                                  disabled={busyId === goal.id}
                                  onClick={() => handleApprove(goal)}
                                >
                                  {t("hse.goals.approve.action")}
                                </HseRowActionButton>
                              ) : null}
                              {canSignHseGoal(goal) ? (
                                <HseRowActionButton
                                  disabled={busyId === goal.id}
                                  onClick={() => handleSign(goal)}
                                >
                                  {t("hse.goals.sign.action")}
                                </HseRowActionButton>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => handleDelete(goal)}
                                disabled={busyId === goal.id}
                                className="rounded-full p-2 text-slate-500 hover:bg-white hover:text-rose-700"
                                aria-label={t("hse.goals.focus.remove")}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </HseRowActions>
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
            </section>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={runWindowPrint}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                <PrinterIcon className="h-4 w-4" />
                {t("hse.goals.print")}
              </button>
            </div>
          </div>
        )}
      </div>
    </HsePageChrome>
  );
};
